<template>
    <div id="chatRoomMainId" class="chat_room_info_main">
        <div class="chat_room_info_wrp">
            <ChatHeader :selected-room-info="selectedRoomInfo" :is-mobile="isMobile" :fallback-avatar="fallbackAvatar"
                @back="hideMenuBarOnMblMethod" @group-info="groupSomeInfoEnabled"
                @search="messageSearchPopupEnableMethod" @file-change="handleFileUpload" @file-click="onInputClick"
                @pin="pinnedChatRoomMethod" @block="blockedUserMethod" />
            <div class="room_messages" :class="{ height_reduce: replyMsgAlertState }">
                <ChatMessageList ref="chatMessageList" :messages="reversedMessages"
                    :selected-room-info="selectedRoomInfo" :self-user-id="selfUserId"
                    :typing-user-info-state="typingUserInfoState" :msg-api-loader="msgApiLoader"
                    :multi-message-option-enabled="multiMessageOptionEnabled" :selected-messages="multipleMessageArr"
                    @scroll="handleScroll" @reply="replyMessageOnChatMethod"
                    @forward="forwardMsgToUserMethod" @delete="handleDeleteMessage"
                    @selection-change="toggleSelectedMessage" @replyMessageFullPreview="replyMessageFullPreviewMethod"  />

                <ChatFileUpload v-if="uploadFile" ref="fileUploadComposer" v-model="fileMessage"
                    :upload-file="uploadFile" :filename="filename" :image-src="imgSrc" :selected-room-info="selectedRoomInfo"
                    :selected-room-id="selectedActvRoomId"
                    :suggestion-list-visible="suggestionUserListStateForFileAttach"
                    :suggestion-user-data="suggestionUserListData" :submitted="submitted"
                    @update:suggestion-user-data="suggestionUserListData = $event" @cancel="cancelUploadFile"
                    @submit="fileMsgSubmitMethod" @message-input="handleFileMessageInput"
                    @keydown="handleFileMessageKeydown" @suggestion-close="suggestionListPopupClosed" />
            </div>

            <ChatReplyPreview v-if="replyMsgAlertState" :reply-message-data="replyMessageData"
                @cancel="cancelReplyMessageOnChatMethod(true)" />

            <ChatMessageComposer v-if="selectedRoomInfo && !uploadFile" ref="messageComposer" v-model="messageContentDraftValue"
                :selected-room-info="selectedRoomInfo" :selected-room-id="selectedActvRoomId"
                :multi-message-option-enabled="multiMessageOptionEnabled"
                :selected-message-count="multipleMessageArr.length" :suggestion-list-visible="suggestionUserListState"
                :suggestion-user-data="suggestionUserListData" :selection-type="selection_type"
                :message-feature-states="messageFeatureStates"
                @update:suggestion-user-data="suggestionUserListData = $event"
                @clear-selection="clearMultipleMsgDataMethod" @forward-selection="forwardMultipleMsgToUserMethod"
                @keydown="handleMessageKeydown" @message-input="handleMessageInput" @paste="captureImgOnPasteMethod"
                @emoji="insertEmojiMethod" @send="sendMessage" @toggle-options="messageFeatureStatesMethod"
                @close-options="closeMessageFeatureMenu" @option-change="selectedOptionMethod"
                @suggestion-close="suggestionListPopupClosed" />
        </div>

        <div v-if="listLoading" class="fixloader listLoading">
            <percentageLoader v-if="uploadProgress > 0 && submitted" :uploadProgressState="uploadProgress" />
        </div>

        <div v-if="messageListResultsDto === null" class="fixloader chatLoader">
            <div class="spinner spinner-1" />
        </div>

        <user-blocked-popup v-if="blockSelectUser" :blockedUserId="blockedUserId"
            :blockedUserEmail="blockedUserEmail" />

        <group-info-popup v-if="groupInfoState" :selectedRoomInfo="someInfoOfGroupState" />

        <msg-delete-popup v-if="deleteMsgPopupState" :deleteMessageDataOBJ="deleteMessageDataObj"
            :roomSocketUrl="chatSocketMethod" />

        <pinned-room-popup v-if="pinnedRoomPopupState" :pinnedRoomID="pinnedRoomId"
            :pinnedRoomSTATUS="pinnedRoomStatus" />

        <forward-msg-popup v-if="forwardMsgPopup" :forwardMsgInfo="multipleMessageArr" :roomSocketUrl="chatSocketMethod"
            :chatRoomInfo="selectedRoomInfo" @forwarded="handleForwardedMessages" />

        <message-search-popup v-if="messageSearchPopupState" :messageSearchForChatInfo="selectedRoomInfo"
            :roomSocketUrl="chatSocketMethod" />

        <reply-message-full-preview-popup v-if="replyMessageFullPreviewState" :message="replyMessageFullPreviewData"/>
    </div>
</template>

<script>
import { bus } from '@/main';
import responsiveMixin from '@/mixins/responsive';
import currentUser from '@/mixins/currentUser';

import chatSocketMixin from '@/mixins/chat/chatSocketMixin';
import chatMessageViewportMixin from '@/mixins/chat/chatMessageViewportMixin';
import chatComposerMixin from '@/mixins/chat/chatComposerMixin';
import chatMessageSelectionMixin from '@/mixins/chat/chatMessageSelectionMixin';
import chatFileUploadMixin from '@/mixins/chat/chatFileUploadMixin';
import chatRoomMessagesMixin from '@/mixins/chat/chatRoomMessagesMixin';
import chatLiveMessagesMixin from '@/mixins/chat/chatLiveMessagesMixin';

const ChatHeader = () => import(
    '@/components/private/workspace/common/ChatHeader'
);

const ChatMessageList = () => import(
    '@/components/private/workspace/common/ChatMessageList'
);

const ChatFileUpload = () => import(
    '@/components/private/workspace/common/ChatFileUpload'
);

const ChatReplyPreview = () => import(
    '@/components/private/workspace/common/ChatReplyPreview'
);

const ChatMessageComposer = () => import(
    '@/components/private/workspace/common/ChatMessageComposer'
);

const PinnedRoomPopup = () => import(
    '@/components/private/workspace/chat-room-pinned-popup/'
);

const PercentageLoader = () => import(
    '@/components/common/percentage-loader-f-file/'
);

const MsgDeletePopup = () => import(
    '@/components/private/workspace/msg-delete-popup/'
);

const MessageSearchPopup = () => import(
    '@/components/private/workspace/message-search-popup/'
);

const ForwardMsgPopup = () => import(
    '@/components/private/workspace/forward-msg-popup/'
);

const GroupInfoPopup = () => import(
    '@/components/private/workspace/group-info-preview-popup'
);

const UserBlockedPopup = () => import(
    '@/components/private/workspace/user-blocked-popup'
);

const ReplyMessageFullPreviewPopup = () => import(
    '@/components/private/workspace/common/ReplyMessageFullPreviewPopup'
);

const FALLBACK_AVATAR = require(
    '@/assets/images/user_circle_avtar.png'
);

export default {
    name: 'chat-info-section',

    components: {
        ReplyMessageFullPreviewPopup,
        ChatHeader,
        ChatMessageList,
        ChatFileUpload,
        ChatReplyPreview,
        ChatMessageComposer,
        'pinned-room-popup': PinnedRoomPopup,
        percentageLoader: PercentageLoader,
        'msg-delete-popup': MsgDeletePopup,
        'message-search-popup': MessageSearchPopup,
        'forward-msg-popup': ForwardMsgPopup,
        'group-info-popup': GroupInfoPopup,
        'user-blocked-popup': UserBlockedPopup
    },

    mixins: [
        responsiveMixin,
        currentUser,
        chatSocketMixin,
        chatMessageViewportMixin,
        chatComposerMixin,
        chatMessageSelectionMixin,
        chatFileUploadMixin,
        chatRoomMessagesMixin,
        chatLiveMessagesMixin
    ],

    props: {
        selectedChatRoomData: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            fallbackAvatar: FALLBACK_AVATAR,

            blockedUserId: '',
            blockedUserEmail: '',
            blockSelectUser: false,

            groupInfoState: false,
            someInfoOfGroupState: null,
            messageSearchPopupState: false,

            pinnedRoomPopupState: false,
            pinnedRoomId: '',
            pinnedRoomStatus: false,

            componentActive: true,
        };
    },

    watch: {
        selectedChatRoomData(newRoom) {
            if (!newRoom) {
                this.clearCurrentRoom();
                return;
            }

            this.selectedRoomInfo = newRoom;
            this.clearMultipleMsgDataMethod();
            this.buildChatRoomMethod(newRoom);
        }
    },

    mounted() {
        this.registerBusListeners();

        if (this.selectedChatRoomData) {
            this.selectedRoomInfo =
                this.selectedChatRoomData;

            this.buildChatRoomMethod(
                this.selectedChatRoomData
            );
        }
    },

    activated() {
        this.componentActive = true;
        this.registerBusListeners();

        if (this.selectedChatRoomData) {
            this.selectedRoomInfo =
                this.selectedChatRoomData;

            this.buildChatRoomMethod(
                this.selectedChatRoomData
            );
        }
    },

    deactivated() {
        this.saveActiveRoomToCache();
        this.componentActive = false;
        this.unregisterBusListeners();
        this.cancelPendingChatRequests();
        this.currentChatRequestId += 1;
        this.isFetchingMessages = false;
        this.isRefreshingMessages = false;
        this.msgApiLoader = false;

        this.stopTypingActivity(
            this.selectedActvRoomId
        );

        this.closeExistingSocket();
    },

    beforeDestroy() {
        this.saveActiveRoomToCache();
        this.componentActive = false;
        this.unregisterBusListeners();
        this.cancelPendingChatRequests();

        this.stopTypingActivity(
            this.selectedActvRoomId
        );

        this.destroyChatSocketClient();
        this.cancelUploadFile();
        this.clearReadStatusRefresh();
        this.clearScheduledRoomCacheSave();

        document.body.classList.remove(
            'body_right_scroll_remove'
        );
    },

    methods: {
        registerBusListeners() {
            if (this._busListeners) {
                return;
            }

            this._busListeners = [
                [
                    'workspaceRouterLeaveEvntBus',
                    this.handleWorkspaceRouterLeave
                ],
                [
                    'typingEventForChatBus',
                    this.handleTypingEvent
                ],
                [
                    'MessageDeleteForChatOfAllNotification',
                    this.handleMessageDeleted
                ],
                [
                    'updateGroupInfoDataBus',
                    this.handleGroupInfoUpdate
                ],
                [
                    'groupInfoStateBus',
                    (state) => {
                        this.groupInfoState =
                            Boolean(state);
                    }
                ],
                [
                    'blockSelectUserBus',
                    (state) => {
                        this.blockSelectUser =
                            Boolean(state);
                    }
                ],
                [
                    'deleteMsgPopupStateBus',
                    (state) => {
                        this.deleteMsgPopupState =
                            Boolean(state);
                    }
                ],
                [
                    'pinnedRoomPopupStateBus',
                    (state) => {
                        this.pinnedRoomPopupState =
                            Boolean(state);
                    }
                ],
                [
                    'forwardMsgPopupBus',
                    this.handleForwardPopupState
                ],
                [
                    'messageSearchPopupStateBus',
                    (state) => {
                        this.messageSearchPopupState =
                            Boolean(state);
                    }
                ],
                [
                    'replyMessageFullPreviewBus',
                    (state) => {
                        this.replyMessageFullPreviewState =
                            Boolean(state);
                    }
                ]
            ];

            this._busListeners.forEach(
                (listener) => {
                    bus.$on(
                        listener[0],
                        listener[1]
                    );
                }
            );
        },

        unregisterBusListeners() {
            if (!this._busListeners) {
                return;
            }

            this._busListeners.forEach(
                (listener) => {
                    bus.$off(
                        listener[0],
                        listener[1]
                    );
                }
            );

            this._busListeners = null;
        },

        handleWorkspaceRouterLeave(data) {
            if (data !== 'Router_leave') {
                return;
            }

            this.saveActiveRoomToCache();

            this.stopTypingActivity(
                this.selectedActvRoomId
            );

            this.closeExistingSocket();
        },

        handleTypingEvent(data) {
            if (!data) {
                this.typingUserInfoState = null;
                return;
            }

            const eventRoomId =
                data.ChatRoomId ||
                data.chat_room ||
                data.room_id;

            if (
                eventRoomId &&
                String(eventRoomId) !==
                    String(
                        this.selectedActvRoomId
                    )
            ) {
                return;
            }

            this.typingUserInfoState =
                data.message === 'stop'
                    ? null
                    : data;
        },

        handleGroupInfoUpdate(data) {
            if (!data) {
                return;
            }

            this.selectedRoomInfo = data;

            if (this.someInfoOfGroupState) {
                this.someInfoOfGroupState = data;
            }
        },

        messageSearchPopupEnableMethod() {
            this.messageSearchPopupState = true;
        },

        blockedUserMethod(
            id,
            firstName,
            lastName
        ) {
            this.blockedUserId = id;

            this.blockedUserEmail = [
                firstName,
                lastName
            ].filter(Boolean).join(' ');

            this.blockSelectUser = true;

            document.body.classList.add(
                'body_right_scroll_remove'
            );
        },

        groupSomeInfoEnabled(data) {
            this.someInfoOfGroupState = data;
            this.groupInfoState = true;
        },

        pinnedChatRoomMethod(
            chatRoomStatus,
            chatRoomId
        ) {
            this.pinnedRoomStatus =
                chatRoomStatus === true ||
                chatRoomStatus === 1 ||
                chatRoomStatus === 'true';

            this.pinnedRoomId = chatRoomId;
            this.pinnedRoomPopupState = true;
        },

        resetChatUIState() {
            this.lastLiveMsgData = null;
            this.clearReadStatusRefresh();
            this.messageContentDraftValue = '';
            this.suggestionUserListState = false;

            this.suggestionUserListStateForFileAttach =
                false;

            this.typingUserInfoState = null;
            this.isRefreshingMessages = false;
            this.msgApiLoader = false;
            this.someInfoOfGroupState = null;
            this.groupInfoState = false;
            this.messageSearchPopupState = false;
            this.messageFeatureStates = false;
            this.cancelReplyMessageOnChatMethod(false);
            this.clearMultipleMsgDataMethod();
            this.cancelUploadFile();

            this.$nextTick(() => {
                if (this.getMessageInputElement()) {
                    this.resetAutoResizeMethod();
                }
            });
        },

        hideMenuBarOnMblMethod() {
            this.saveActiveRoomToCache();

            this.stopTypingActivity(
                this.selectedActvRoomId
            );

            this.closeExistingSocket();
            this.selectedActvRoomId = '';

            const sidepanel =
                document.getElementById(
                    'sidepanel'
                );

            const chatContent =
                document.getElementById(
                    'chatRoomMainId'
                );

            if (sidepanel) {
                sidepanel.classList.remove(
                    'd-none'
                );
            }

            if (chatContent) {
                chatContent.classList.remove(
                    'd-block'
                );
            }

            document.querySelectorAll(
                '.chat_user_listed'
            ).forEach((element) => {
                element.classList.remove('active');
            });

            bus.$emit(
                'hideChatSectionInMblBus',
                true
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.chat_room_info_main {
    width: 55%;
    transition: all ease-in-out 0.5s;
    -webkit-transition: all ease-in-out 0.5s;
    -moz-transition: all ease-in-out 0.5s;
    float: left;
    height: 100%;
    overflow: hidden;
    position: relative;
    border-right: 1px solid rgba(35, 35, 35, 0.4);
    border-bottom: 1px solid rgba(35, 35, 35, 0.4);
    background: rgba(0, 0, 0, .8);

    @media only screen and (max-width:769px) {
        width: 100%;
        display: none;
    }


    .listLoading {
        z-index: 1000;
    }

    .height_reduce {
        .messages_box {
            height: calc(100dvh - 284px) !important;
        }
    }
}
</style>